import { POINTS_TYPE, POINTS_VALIDITY_TYPE } from '@/constants/accelerationTable'
import { CAMPAIGN_BRAND_CODES } from '@/constants/campaignBrands'

import type { AccelerationFormFields } from '../AccelerationForm.types'
import { normalizeCampaignBrandFlags } from './brandForApi'
import { normalizeAccelerationContentDetails } from './detailRichItems'

const toStringOrUndefined = (value: unknown): string | undefined =>
  value !== undefined && value !== null ? String(value) : undefined

const resolvePointsTypeFromLegacyFields = (points: CampaignQueryShape['points']) => {
  if (!points) return undefined

  if (points.type) return points.type

  if (points.countOfDays !== undefined && points.countOfDays !== null && String(points.countOfDays).trim() !== '') {
    return POINTS_TYPE.DAYS_CALENDAR
  }

  if ((points.startDate ?? '').trim() || (points.endDate ?? '').trim()) {
    return POINTS_TYPE.DEFAULT
  }

  if ((points.description ?? '').trim()) {
    return POINTS_TYPE.TIER_RULE
  }

  return undefined
}

const resolvePointsValidityType = (pointsType?: string) => {
  if (!pointsType) return undefined

  return pointsType === POINTS_TYPE.DEFAULT ? POINTS_VALIDITY_TYPE.FIXED : POINTS_VALIDITY_TYPE.FLEXIBLE
}

const buildSegmentation = (segmentation: CampaignQueryShape['segmentation']) => {
  if (!segmentation) return undefined

  return {
    campaignType: segmentation.campaignType ?? undefined,
    audienceType: segmentation.audienceType ?? undefined
  }
}

const buildPoints = (points: CampaignQueryShape['points']) => {
  if (!points) return undefined

  const resolvedPointsType = resolvePointsTypeFromLegacyFields(points)

  return {
    type: resolvedPointsType,
    startDate: points.startDate ?? undefined,
    endDate: points.endDate ?? undefined,
    description: points.description ?? undefined,
    countOfDays: toStringOrUndefined(points.countOfDays)
  }
}

const buildContent = (content: CampaignQueryShape['content']) => {
  if (!content) return undefined

  return {
    title: content.title ?? '',
    description: content.description ?? '',
    imageUrl: content.imageUrl ?? '',
    imageKey: content.imageKey ?? '',
    buttonText: content.buttonText ?? '',
    buttonUrl: content.buttonUrl ?? '',
    showModal: Boolean(content.showModal),
    details: normalizeAccelerationContentDetails({
      pageTitle: content.details?.pageTitle ?? '',
      pageDescription: content.details?.pageDescription ?? '',
      steps: content.details?.steps ?? undefined,
      rules: content.details?.rules ?? undefined
    })
  }
}

const buildContentByBrand = (contentByBrand: CampaignQueryShape['contentByBrand']) => {
  if (!contentByBrand) return {}

  return CAMPAIGN_BRAND_CODES.reduce<NonNullable<AccelerationFormFields['contentByBrand']>>((acc, brandCode) => {
    const buttonUrl = contentByBrand[brandCode]?.buttonUrl
    if (buttonUrl) {
      acc[brandCode] = { buttonUrl }
    }
    return acc
  }, {})
}

/** Resposta parcial de getCampaign — mantenha alinhado com GET_CAMPAIGN_BY_ID */
export interface CampaignQueryShape {
  campaignId?: string | null
  name?: string | null
  segmentation?: {
    campaignType?: string | null
    audienceType?: string | null
  } | null
  type?: string | null
  point?: number | string | null
  multiplier?: number | string | null
  startDate?: string | null
  endDate?: string | null
  points?: {
    type?: string | null
    startDate?: string | null
    endDate?: string | null
    description?: string | null
    countOfDays?: number | string | null
  } | null
  status?: AccelerationFormFields['status']
  responsible?: string | null
  createdAt?: string | null
  changeHistory?: AccelerationFormFields['changeHistory']
  content?: {
    title?: string | null
    description?: string | null
    imageUrl?: string | null
    imageKey?: string | null
    buttonText?: string | null
    buttonUrl?: string | null
    showModal?: boolean | null
    details?: {
      pageTitle?: string | null
      pageDescription?: string | null
      steps?: string[] | null
      rules?: string[] | null
    } | null
  } | null
  brand?: {
    BOT?: boolean | null
    QDB?: boolean | null
    BLZ?: boolean | null
    EUD?: boolean | null
  } | null
  contentByBrand?: {
    BOT?: { buttonUrl?: string | null } | null
    QDB?: { buttonUrl?: string | null } | null
    BLZ?: { buttonUrl?: string | null } | null
    EUD?: { buttonUrl?: string | null } | null
  } | null
}

export const mapCampaignQueryToFormFields = (
  campaign: CampaignQueryShape | null | undefined
): AccelerationFormFields => {
  if (!campaign) return {}

  const points = buildPoints(campaign.points)

  return {
    campaignId: campaign.campaignId ?? undefined,
    name: campaign.name ?? undefined,
    segmentation: buildSegmentation(campaign.segmentation),
    type: campaign.type ?? undefined,
    point: toStringOrUndefined(campaign.point),
    multiplier: toStringOrUndefined(campaign.multiplier),
    startDate: campaign.startDate ?? undefined,
    endDate: campaign.endDate ?? undefined,
    points,
    pointsValidityType: resolvePointsValidityType(points?.type),
    status: campaign.status,
    responsible: campaign.responsible ?? undefined,
    createdAt: campaign.createdAt ?? undefined,
    changeHistory: campaign.changeHistory,
    brand: normalizeCampaignBrandFlags(campaign.brand),
    contentByBrand: buildContentByBrand(campaign.contentByBrand),
    content: buildContent(campaign.content)
  }
}
